import * as React from 'react';
import {Link} from 'react-router-dom';

import {getApiTypeFromGraphQLTypename} from 'nutshell-core/utils';

import {PeepTwoLine} from 'shells/peep/peep-two-line';

import type {
    AccountSearchResultByType as AccountSearchResultFragment,
    ContactSearchResultByType as ContactSearchResultFragment,
} from 'nutshell-graphql-types';

import styles from './search-results.module.css';

type Props = {
    peep: AccountSearchResultFragment | ContactSearchResultFragment;
    isActive: boolean;
};

const relatedEntityName = (
    peep: AccountSearchResultFragment | ContactSearchResultFragment
): string | null | undefined => {
    if (peep.__typename === 'Account' && peep.primaryContact) {
        return peep.primaryContact.name;
    }

    if (peep.__typename === 'Contact' && peep.accounts) {
        return peep.accounts.primary && peep.accounts.primary.name;
    }
};

const secondaryInfo = (
    peep: AccountSearchResultFragment | ContactSearchResultFragment
): string | null | undefined => {
    if (peep.__typename === 'Account') {
        const primaryAddress =
            peep.postalAddresses &&
            peep.postalAddresses.primary &&
            peep.postalAddresses.primary.value;

        // If we've got an address, use that
        if (primaryAddress) {
            if (primaryAddress.city && primaryAddress.postalCode) {
                return `${primaryAddress.city}, ${primaryAddress.postalCode}`;
            } else if (primaryAddress.city) {
                return primaryAddress.city;
            } else {
                return primaryAddress.postalCode;
            }
        }

        // Next, try a phone number
        return peep.phones.primary && peep.phones.primary.value.numberFormatted;
    }

    if (peep.__typename === 'Contact') {
        const primaryEmailAddress =
            peep.emailAddresses &&
            peep.emailAddresses.primary &&
            peep.emailAddresses.primary.value.address;

        const primaryPhoneNumber = peep.phones.primary && peep.phones.primary.value.numberFormatted;

        return primaryEmailAddress || primaryPhoneNumber;
    }
};

export const SearchResultPeep = ({peep, isActive}: Props) => {
    return (
        // @ts-expect-error
        <Link
            to={peep.htmlUrlPath}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
            className={isActive ? styles['search-result--fake-focus'] : styles['search-result']}
        >
            <PeepTwoLine
                name={peep.name}
                avatarUrl={peep.avatarUrl}
                initials={peep.initials}
                type={getApiTypeFromGraphQLTypename(peep.__typename)}
                htmlUrl={peep.htmlUrlPath}
                isLinkable={false}
                relatedEntityName={relatedEntityName(peep)}
            />
            <div className={styles['secondary-info']}>{secondaryInfo(peep)}</div>
        </Link>
    );
};
